import type { MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

import { AppError } from '@app/errors/AppError'

import { withCache } from '@app/utils/cache'
import { createSelector } from '@app/utils/createSelector'

import type { ApiActionCallback } from '@app/packages/api/utils'
import { getApiActionDescriptor } from '@app/packages/api/utils'
import type { V3HelpTopics } from '@app/packages/api/v3/types/lib'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callApi, withAnonymous } from '@app/store/actions/api'
import { getServerCache } from '@app/store/actions/contexts/serverCache'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import { intlSelector } from './intl'
import { routingDateSelector } from './routing'

const topicsSlice = createReduxSlice<SliceState<V3HelpTopics>>('help_topics')

export const helpTopicsSelector = createSelector([topicsSlice.selector, routingDateSelector], (sliceState, date) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: topicsSlice,
      date: { date, expiresInMinutes: 60 },
      load: async dispatch => await dispatch(fetchForEveryone(api => api.getHelpTopics(), 'Help topics fetch failed', messages.topics_failed)),
    })
  )
)

/** An admin's bearer would get private articles back, and one server cache entry serves every viewer. */
function fetchForEveryone<R>(pick: (api: HelpApi) => ApiActionCallback<R>, failure: string, localizedFailure: MessageDescriptor) {
  return createThunk(async (dispatch, getState) => {
    try {
      const apiAction = pick(await import('@app/packages/api/v3/actions/help'))
      const key = `e5fb3245-c018-4662-a074-261cdecb8dd6 ${getApiActionDescriptor(apiAction).label}`
      return await withCache(dispatch(getServerCache()), key, () => dispatch(withAnonymous(callApi(apiAction))))
    } catch (e) {
      throw new AppError(failure).withCause(e).withLocalizedMessage(intlSelector(getState()).formatMessage(localizedFailure))
    }
  })
}

type HelpApi = typeof import('@app/packages/api/v3/actions/help')

const messages = defineMessages({
  topics_failed: { id: "store.slices.help.messages.topics_failed.2529", defaultMessage: 'Не удалось загрузить статьи справки' },
})
